import React, { useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js';
import { plotlyResizeIfDisplayed } from '../utils/plotlySafeResize';
import './JackStrokeBalanceChart.css';

/** Same as public/jack-benchmark.html balance tab scatter markers */
const COLOR_FRONT = '#3182ce';
const COLOR_REAR = '#e53e3e';

function getFiniteMax(values) {
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const n = Number(values[i]);
    if (!Number.isFinite(n)) continue;
    if (n > max) max = n;
  }
  return max === -Infinity ? null : max;
}

const plotlyConfig = {
  responsive: true,
  displayModeBar: false,
};

function calculateLinearRegression(xData, yData) {
  if (!xData || !yData || xData.length === 0 || xData.length !== yData.length) {
    return null;
  }
  const n = xData.length;
  const xMean = xData.reduce((sum, x) => sum + x, 0) / n;
  const yMean = yData.reduce((sum, y) => sum + y, 0) / n;
  let covariance = 0;
  let xVariance = 0;
  for (let i = 0; i < n; i++) {
    const xDiff = xData[i] - xMean;
    const yDiff = yData[i] - yMean;
    covariance += xDiff * yDiff;
    xVariance += xDiff * xDiff;
  }
  if (xVariance === 0) return null;
  const slope = covariance / xVariance;
  const intercept = yMean - slope * xMean;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    const yPred = slope * xData[i] + intercept;
    ssRes += (yData[i] - yPred) ** 2;
    ssTot += (yData[i] - yMean) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;
  const slopeStr = slope.toFixed(3);
  const interceptStr = Math.abs(intercept).toFixed(1);
  const sign = intercept >= 0 ? '+' : '-';
  const equation = `y = ${slopeStr}x ${sign} ${interceptStr}`;
  return { slope, intercept, rSquared, equation };
}

function axisDark() {
  return {
    title: { font: { color: '#9aa0a6' } },
    gridcolor: 'rgba(255, 255, 255, 0.08)',
    zeroline: false,
    color: '#888',
    linecolor: 'rgba(255, 255, 255, 0.15)',
  };
}

function buildBalancePlotPayload(analysisResults, strokeType, speedFilter, frontThreshold, rearShockThreshold) {
  const stats = analysisResults?.stats;
  const frontVel = stats?.front?.velocity;
  const rearVel = stats?.rear?.velocity;
  if (!frontVel?.allStrokes || !rearVel?.allStrokes) {
    return { kind: 'no_strokes_meta' };
  }

  let frontStrokes = [];
  let rearStrokes = [];
  if (strokeType === 'both') {
    frontStrokes = [...(frontVel.allStrokes.compression || []), ...(frontVel.allStrokes.rebound || [])];
    rearStrokes = [...(rearVel.allStrokes.compression || []), ...(rearVel.allStrokes.rebound || [])];
  } else if (strokeType === 'compression') {
    frontStrokes = frontVel.allStrokes.compression || [];
    rearStrokes = rearVel.allStrokes.compression || [];
  } else {
    frontStrokes = frontVel.allStrokes.rebound || [];
    rearStrokes = rearVel.allStrokes.rebound || [];
  }

  const frontTh = Number.isFinite(Number(frontThreshold)) ? Number(frontThreshold) : 1000;
  const rearTh = Number.isFinite(Number(rearShockThreshold)) ? Number(rearShockThreshold) : 350;

  if (speedFilter === 'low') {
    frontStrokes = frontStrokes.filter((s) => s.maxVelocity < frontTh);
    rearStrokes = rearStrokes.filter((s) => (s.shockVelocity ?? s.maxVelocity) < rearTh);
  } else if (speedFilter === 'high') {
    frontStrokes = frontStrokes.filter((s) => s.maxVelocity >= frontTh);
    rearStrokes = rearStrokes.filter((s) => (s.shockVelocity ?? s.maxVelocity) >= rearTh);
  }

  if (frontStrokes.length === 0 && rearStrokes.length === 0) {
    return { kind: 'empty', speedFilter, frontTh, rearTh };
  }

  const traces = [];

  if (frontStrokes.length > 0) {
    const frontDepths = frontStrokes.map((s) => s.depth);
    const frontVelocities = frontStrokes.map((s) => s.maxVelocity);
    traces.push({
      x: frontDepths,
      y: frontVelocities,
      name: 'Front',
      type: 'scatter',
      mode: 'markers',
      marker: { color: COLOR_FRONT, size: 6, opacity: 0.5 },
      hovertemplate: 'Depth: %{x:.1f} mm<br>Velocity: %{y:.0f} mm/s<extra></extra>',
    });
    const frontRegression = calculateLinearRegression(frontDepths, frontVelocities);
    if (frontRegression) {
      const xMax = getFiniteMax(frontDepths) ?? 0;
      const xRange = [0, xMax * 1.05];
      traces.push({
        x: xRange,
        y: xRange.map((x) => frontRegression.slope * x + frontRegression.intercept),
        name: 'Front trend',
        type: 'scatter',
        mode: 'lines',
        line: { color: COLOR_FRONT, width: 2, dash: 'dash' },
        hoverinfo: 'skip',
      });
    }
  }

  if (rearStrokes.length > 0) {
    const rearDepths = rearStrokes.map((s) => s.depth);
    const rearVelocities = rearStrokes.map((s) => s.maxVelocity);
    traces.push({
      x: rearDepths,
      y: rearVelocities,
      name: 'Rear',
      type: 'scatter',
      mode: 'markers',
      marker: { color: COLOR_REAR, size: 6, opacity: 0.5 },
      hovertemplate: 'Depth: %{x:.1f} mm<br>Velocity: %{y:.0f} mm/s<extra></extra>',
    });
    const rearRegression = calculateLinearRegression(rearDepths, rearVelocities);
    if (rearRegression) {
      const xMax = getFiniteMax(rearDepths) ?? 0;
      const xRange = [0, xMax * 1.05];
      traces.push({
        x: xRange,
        y: xRange.map((x) => rearRegression.slope * x + rearRegression.intercept),
        name: 'Rear trend',
        type: 'scatter',
        mode: 'lines',
        line: { color: COLOR_REAR, width: 2, dash: 'dash' },
        hoverinfo: 'skip',
      });
    }
  }

  const axis = axisDark();
  const layout = {
    paper_bgcolor: '#0a0a0a',
    plot_bgcolor: '#0a0a0a',
    font: { color: '#bbb' },
    margin: { t: 36, r: 20, b: 44, l: 56 },
    xaxis: { ...axis, title: { text: 'Stroke depth (mm)', ...axis.title } },
    yaxis: { ...axis, title: { text: 'Max velocity (mm/s)', ...axis.title } },
    legend: { orientation: 'h', y: 1.12, x: 0.5, xanchor: 'center', font: { color: '#ccc', size: 11 } },
    hovermode: 'closest',
  };

  return { kind: 'plot', traces, layout };
}

/**
 * Stroke depth vs max velocity (Plotly), same data and filters as public/jack-benchmark.html tab-balance.
 */
/** Fixed low/high speed split (mm/s); matches jack-benchmark.html defaults. */
const DEFAULT_FRONT_SPEED_SPLIT_MM_S = 1000;
const DEFAULT_REAR_SHOCK_SPEED_SPLIT_MM_S = 350;

export default function JackStrokeBalanceChart({ analysisResults = null }) {
  const plotRef = useRef(null);
  const [strokeType, setStrokeType] = useState('both');
  const [speedFilter, setSpeedFilter] = useState('both');

  const payload = useMemo(
    () =>
      buildBalancePlotPayload(
        analysisResults,
        strokeType,
        speedFilter,
        DEFAULT_FRONT_SPEED_SPLIT_MM_S,
        DEFAULT_REAR_SHOCK_SPEED_SPLIT_MM_S,
      ),
    [analysisResults, strokeType, speedFilter],
  );

  useEffect(() => {
    const el = plotRef.current;
    if (!el) return undefined;

    const purge = () => {
      try {
        Plotly.purge(el);
      } catch (_) {
        /* ignore */
      }
      el.innerHTML = '';
    };

    if (payload.kind === 'no_strokes_meta') {
      purge();
      return undefined;
    }

    if (payload.kind === 'empty') {
      purge();
      el.innerHTML = '<p class="jack-stroke-balance-empty">No strokes match the current filters.</p>';
      return undefined;
    }

    if (payload.kind === 'plot') {
      Plotly.react(el, payload.traces, payload.layout, plotlyConfig).catch(() => {});
    }

    const root = el.closest('.jack-stroke-balance');
    const onResize = () => {
      plotlyResizeIfDisplayed(el);
    };
    let ro = null;
    if (root) {
      ro = new ResizeObserver(onResize);
      ro.observe(root);
      window.addEventListener('resize', onResize);
      requestAnimationFrame(onResize);
    }

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      purge();
    };
  }, [payload]);

  const stats = analysisResults?.stats;
  const frontVel = stats?.front?.velocity;
  const rearVel = stats?.rear?.velocity;
  if (!frontVel?.allStrokes || !rearVel?.allStrokes) {
    return <p className="suspension-jack-error">No stroke data available for balance.</p>;
  }

  return (
    <div className="jack-stroke-balance">
      <div className="jack-stroke-balance-toolbar" aria-label="Balance chart filters">
        <label htmlFor="jack-balance-stroke-type">
          Strokes
          <select
            id="jack-balance-stroke-type"
            value={strokeType}
            onChange={(e) => setStrokeType(e.target.value)}
          >
            <option value="both">Both</option>
            <option value="compression">Compression only</option>
            <option value="rebound">Rebound only</option>
          </select>
        </label>
        <label htmlFor="jack-balance-speed-filter">
          Speed
          <select
            id="jack-balance-speed-filter"
            value={speedFilter}
            onChange={(e) => setSpeedFilter(e.target.value)}
          >
            <option value="both">Both</option>
            <option value="low">Low speed</option>
            <option value="high">High speed</option>
          </select>
        </label>
      </div>
      <div ref={plotRef} className="jack-stroke-balance-plot" />
    </div>
  );
}
